/**
 * The filters the Suppliers and All Tasks lists share, and how they travel:
 * in the page URL, so a filtered view can be bookmarked or sent to someone, and
 * to the API.
 *
 * The stage keys and labels mirror server/utils/applicationStage.js, which is
 * what the server filters and labels rows with.
 */

import { CONTRACT_OPTIONS, COMPLETION_OPTIONS } from './contractApplicability';

export const STAGE_OPTIONS = [
  { value: 'draft', label: 'Draft (with supplier)' },
  { value: 'procurement', label: 'Procurement review' },
  { value: 'supplier_procurement', label: 'Supplier answering procurement' },
  { value: 'supplier_legal', label: 'Supplier answering legal' },
  { value: 'legal', label: 'Legal review' },
  { value: 'contract_upload', label: 'Contract upload' },
  { value: 'closed', label: 'Closed' },
];

export const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_procurement', label: 'Pending Procurement' },
  { value: 'more_info_required', label: 'More Info Required' },
  { value: 'pending_legal', label: 'Pending Legal' },
  { value: 'pending_contract_upload', label: 'Prequalified and Pending Contract Upload' },
  { value: 'approved', label: 'Approved' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

export const stageLabelFor = (stage) => STAGE_OPTIONS.find((s) => s.value === stage)?.label || '-';

export const EMPTY_FILTERS = {
  status: '', stage: '', department: '', submittedFrom: '', submittedTo: '', contract: '', completion: '',
};

const KEYS = Object.keys(EMPTY_FILTERS);
const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
const VALID = {
  status: (v) => STATUS_OPTIONS.some((o) => o.value === v),
  stage: (v) => STAGE_OPTIONS.some((o) => o.value === v),
  department: (v) => v.trim() !== '',
  submittedFrom: isDay,
  submittedTo: isDay,
  contract: (v) => CONTRACT_OPTIONS.some((o) => o.value === v),
  completion: (v) => COMPLETION_OPTIONS.some((o) => o.value === v),
};

/** Filters from the URL. A value the page does not offer is dropped, not sent. */
export const filtersFromSearchParams = (searchParams) => {
  const out = { ...EMPTY_FILTERS };
  for (const key of KEYS) {
    const v = searchParams.get(key) || '';
    if (v && VALID[key](v)) out[key] = v;
  }
  return out;
};

/** The URL for `filters`, keeping any unrelated params already on it. */
export const filtersToSearchParams = (filters, current = new URLSearchParams()) => {
  const next = new URLSearchParams(current);
  for (const key of KEYS) {
    if (filters[key]) next.set(key, filters[key]);
    else next.delete(key);
  }
  return next;
};

/**
 * The API params for `filters`. A date range goes with this browser's time zone
 * offset, so the server counts a day as the viewer's day rather than UTC's.
 */
export const filtersToApiParams = (filters) => {
  const params = {};
  for (const key of KEYS) if (filters[key]) params[key] = filters[key];
  const day = filters.submittedFrom || filters.submittedTo;
  if (day) params.tzOffset = new Date(`${day}T00:00:00`).getTimezoneOffset();
  return params;
};

export const hasActiveFilters = (filters) => KEYS.some((key) => Boolean(filters[key]));
